// src/components/admin/RosterUpdatePanel.tsx - Admin panel for managing roster updates
import React, { useState, useEffect } from 'react';
import { 
  CloudArrowDownIcon, 
  PlayIcon, 
  StopIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';

interface RosterUpdateStatus {
  background_service_running: boolean;
  last_update: string | null;
  update_interval_hours: number;
  next_scheduled_update: string;
}

interface RosterChanges {
  time_range_hours: number;
  summary: {
    total_updates: number;
    new_players: number;
    deactivated_players: number;
  };
  recent_updates: Array<{
    id: number;
    name: string;
    position: string;
    team: string;
    updated_at: string;
    is_active: boolean;
  }>;
  new_players: Array<{
    id: number;
    name: string;
    position: string;
    team: string;
    created_at: string;
  }>;
  deactivated_players: Array<{
    id: number;
    name: string;
    position: string;
    team: string;
    deactivated_at: string;
  }>;
}

const RosterUpdatePanel: React.FC = () => {
  const [status, setStatus] = useState<RosterUpdateStatus | null>(null);
  const [recentChanges, setRecentChanges] = useState<RosterChanges | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  useEffect(() => {
    loadStatus();
    loadRecentChanges();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadStatus();
      setLastRefresh(new Date());
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    try {
      const statusData = await apiService.getRosterUpdateStatus();
      setStatus(statusData);
    } catch (error: any) {
      console.error('Failed to load roster update status:', error);
      setError(error.message);
    }
  };

  const loadRecentChanges = async () => {
    try {
      const changesData = await apiService.getRecentRosterChanges(24);
      setRecentChanges(changesData);
    } catch (error: any) {
      console.error('Failed to load recent changes:', error);
    }
  };

  const triggerManualUpdate = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await apiService.triggerManualRosterUpdate();
      alert('Roster update started in background! Check back in a few minutes.');
      await loadStatus();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleBackgroundService = async () => {
    setLoading(true);
    
    try {
      if (status?.background_service_running) {
        await apiService.stopBackgroundRosterUpdates();
      } else {
        await apiService.startBackgroundRosterUpdates();
      }
      await loadStatus();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getTimeSince = (dateString: string) => {
    const diff = Date.now() - new Date(dateString).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ago`;
    }
    return `${minutes}m ago`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-secondary-900">NFL Roster Updates</h2>
          <p className="text-secondary-600">Manage live roster synchronization with NFL APIs</p>
        </div>
        <div className="text-sm text-secondary-500">
          Last refreshed: {lastRefresh.toLocaleTimeString()}
        </div>
      </div>

      {error && (
        <div className="alert-danger">
          <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {/* Status Card */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Update Service Status</h3>
            <button
              onClick={loadStatus}
              className="btn-outline-secondary btn-sm"
              disabled={loading}
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
        <div className="card-body">
          {status ? (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`w-3 h-3 rounded-full ${status.background_service_running ? 'bg-success-500' : 'bg-danger-500'}`}></div>
                  <span className="font-medium">
                    Background Service: {status.background_service_running ? 'Running' : 'Stopped'}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Update Interval:</span>
                    <span>{status.update_interval_hours} hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Last Update:</span>
                    <span>
                      {status.last_update ? getTimeSince(status.last_update) : 'Never'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={triggerManualUpdate}
                  disabled={loading}
                  className="btn-primary w-full"
                >
                  <CloudArrowDownIcon className="w-4 h-4 mr-2" />
                  {loading ? 'Starting Update...' : 'Manual Update Now'}
                </button>
                
                <button
                  onClick={toggleBackgroundService}
                  disabled={loading}
                  className={`w-full ${status.background_service_running ? 'btn-danger' : 'btn-success'}`}
                >
                  {status.background_service_running ? (
                    <>
                      <StopIcon className="w-4 h-4 mr-2" />
                      Stop Background Service
                    </>
                  ) : (
                    <>
                      <PlayIcon className="w-4 h-4 mr-2" />
                      Start Background Service
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner w-6 h-6 mr-3"></div>
              <span>Loading status...</span>
            </div>
          )}
        </div>
      </div>

      {/* Recent Changes */}
      {recentChanges && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Recent Changes (24h)</h3>
              <button
                onClick={loadRecentChanges}
                className="btn-outline-secondary btn-sm"
              >
                <ArrowPathIcon className="w-4 h-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
          <div className="card-body">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="stat-card">
                <div className="stat-value text-secondary-700">{recentChanges.summary.total_updates}</div>
                <div className="stat-label">Updates</div>
              </div>
              <div className="stat-card">
                <div className="stat-value text-success-600">{recentChanges.summary.new_players}</div>
                <div className="stat-label">New Players</div>
              </div>
              <div className="stat-card">
                <div className="stat-value text-danger-600">{recentChanges.summary.deactivated_players}</div>
                <div className="stat-label">Deactivated</div>
              </div>
            </div>

            {/* Detailed Changes */}
            <div className="space-y-6">
              {/* New Players */}
              {recentChanges.new_players.length > 0 && (
                <div>
                  <h4 className="font-medium text-secondary-900 mb-3 flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-success-500 mr-2" />
                    New Players Added
                  </h4>
                  <div className="space-y-2">
                    {recentChanges.new_players.map((player) => (
                      <div key={player.id} className="flex items-center justify-between p-3 bg-success-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            player.position === 'K' ? 'bg-secondary-900 text-gold' : 'bg-success-100 text-success-800'
                          }`}>
                            {player.position}
                          </span>
                          <span className="font-medium">{player.name}</span>
                          <span className="text-sm text-secondary-600">{player.team}</span>
                        </div>
                        <span className="text-sm text-success-600">
                          {getTimeSince(player.created_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Updates */}
              {recentChanges.recent_updates.length > 0 && (
                <div>
                  <h4 className="font-medium text-secondary-900 mb-3 flex items-center">
                    <ClockIcon className="w-5 h-5 text-secondary-500 mr-2" />
                    Recent Updates
                  </h4>
                  <div className="space-y-2">
                    {recentChanges.recent_updates.slice(0, 5).map((player) => (
                      <div key={player.id} className="flex items-center justify-between p-3 bg-secondary-100 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            player.position === 'K' ? 'bg-secondary-900 text-gold' : 'bg-success-100 text-success-800'
                          }`}>
                            {player.position}
                          </span>
                          <span className="font-medium">{player.name}</span>
                          <span className="text-sm text-secondary-600">{player.team}</span>
                          {!player.is_active && (
                            <span className="text-xs bg-danger-100 text-danger-800 px-2 py-1 rounded">Inactive</span>
                          )}
                        </div>
                        <span className="text-sm text-secondary-700">
                          {getTimeSince(player.updated_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deactivated Players */}
              {recentChanges.deactivated_players.length > 0 && (
                <div>
                  <h4 className="font-medium text-secondary-900 mb-3 flex items-center">
                    <ExclamationTriangleIcon className="w-5 h-5 text-danger-500 mr-2" />
                    Deactivated Players
                  </h4>
                  <div className="space-y-2">
                    {recentChanges.deactivated_players.map((player) => (
                      <div key={player.id} className="flex items-center justify-between p-3 bg-danger-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            player.position === 'K' ? 'bg-secondary-900 text-gold' : 'bg-success-100 text-success-800'
                          }`}>
                            {player.position}
                          </span>
                          <span className="font-medium">{player.name}</span>
                          <span className="text-sm text-secondary-600">{player.team}</span>
                        </div>
                        <span className="text-sm text-danger-600">
                          {getTimeSince(player.deactivated_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Changes */}
              {recentChanges.summary.total_updates === 0 && (
                <div className="text-center py-8 text-secondary-500">
                  <ClockIcon className="w-12 h-12 mx-auto mb-3 text-secondary-300" />
                  <p>No roster changes in the last 24 hours</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* API Information */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Data Sources</h3>
        </div>
        <div className="card-body">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-secondary-900 mb-2">Primary API</h4>
              <p className="text-sm text-secondary-600 mb-2">ESPN NFL API</p>
              <ul className="text-xs text-secondary-500 space-y-1">
                <li>• Real-time roster data</li>
                <li>• Player stats and info</li>
                <li>• Team information</li>
                <li>• No API key required</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-secondary-900 mb-2">Backup APIs</h4>
              <p className="text-sm text-secondary-600 mb-2">NFL.com, SportsData.io</p>
              <ul className="text-xs text-secondary-500 space-y-1">
                <li>• Automatic failover</li>
                <li>• Rate limiting protection</li>
                <li>• Data validation</li>
                <li>• Error handling</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Instructions */}
      <div className="bg-secondary-100 border border-secondary-300 rounded-lg p-4">
        <h4 className="font-semibold text-secondary-900 mb-2">💡 How It Works</h4>
        <div className="text-sm text-secondary-800 space-y-2">
          <p><strong>Manual Updates:</strong> Immediately fetch the latest roster data from NFL APIs</p>
          <p><strong>Background Service:</strong> Automatically updates rosters every 6 hours</p>
          <p><strong>Smart Updates:</strong> Only updates changed information to minimize API calls</p>
          <p><strong>Kickers & Punters Only:</strong> Filters for relevant positions automatically</p>
        </div>
      </div>
    </div>
  );
};

export default RosterUpdatePanel;